import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Grid } from '@mui/material';
import { PAGE_TEXT } from '../lang';
import { type ADDITIONAL_DOCS_TYPE } from '../constants';
import {
  Accordion,
  FilesPanel,
  type FileData,
  Alert,
  type AlertDetails,
  Button,
} from '../../../../../components';
import { FILE_MANAGE_ORDER } from '../../../../../components/FilesPanel';
import {
  type AdditionalDocFormData,
  addApplicationItem,
  useUpdateAdditionalDocument,
} from '../../../graphql/ApplicationForm';
import { handleFileRename } from '../../../../../utils/handleFileRename';

type RequiredDocumentInfoDisplay = {
  title: string;
  description: string;
  fileName: string;
  filesPanelTitle: string;
  filesPanelCaption: string;
  supplementText?: string;
};

type RequiredDocumentProps = {
  applicationNo: string;
  infoDisplay: RequiredDocumentInfoDisplay;
  fileType: ADDITIONAL_DOCS_TYPE;
  requiredDocumentData: AdditionalDocFormData[];
  enableEdit: boolean;
  enableDelete: boolean;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

export const RequiredDocument: React.FunctionComponent<
  RequiredDocumentProps
> = props => {
  const {
    applicationNo,
    infoDisplay,
    requiredDocumentData,
    enableEdit,
    fileType,
    refetchApplication,
    handleLoading,
    enableDelete,
  } = props;
  const {
    title,
    description,
    fileName,
    filesPanelTitle,
    filesPanelCaption,
    supplementText = '',
  } = infoDisplay;

  const isCreateMode = requiredDocumentData.length === 0;

  const [requiredDocument, setRequiredDocument] = useState<FileData[]>(
    isCreateMode ? [] : (requiredDocumentData[0]?.supportingDocuments ?? []),
  );

  const { applicationId } = useParams();

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const createAdditionalDocument = addApplicationItem(
    'additionalDocuments',
    refetchApplication,
  );
  const updateAdditionalDocument = useUpdateAdditionalDocument();

  const handleSubmitRequiredDocument = async (): Promise<void> => {
    if (!requiredDocument.length) {
      setOpenAlert(true);
      setAlertDetails({
        alertContent: `Please upload at least one ${title}.`,
        alertType: 'warning',
      });
      return;
    }

    await handleLoading(async () => {
      try {
        const inputValue = {
          documentType: fileType,
          supportingDocuments: requiredDocument.map((doc, index) => {
            const { name, ...other } = doc;
            return {
              name: handleFileRename(applicationNo, fileName, index + 1),
              ...other,
            };
          }),
        };
        if (isCreateMode) {
          await createAdditionalDocument({
            variables: {
              id: applicationId,
              input: {
                additionalDocument: { ...inputValue },
              },
            },
          });
        } else {
          await updateAdditionalDocument({
            variables: {
              id: requiredDocumentData?.[0]?._id,
              ...inputValue,
            },
          });
        }
        setAlertDetails({
          alertContent: `Saved ${title}(s) Successfully.`,
          alertType: 'success',
        });
        refetchApplication();
      } catch (error) {
        setAlertDetails({
          alertContent: 'Error Occurs.' + (error as Error)?.message,
          alertType: 'error',
        });
      }
    });
    setOpenAlert(true);
  };

  const handleUploadFile = (order: number, id: string): boolean => {
    const newRequiredDocument: FileData[] = [...requiredDocument];
    switch (order) {
      case FILE_MANAGE_ORDER.UPLOAD: {
        setRequiredDocument([
          ...newRequiredDocument,
          {
            id,
            name: '',
            link: '',
          },
        ]);
        break;
      }
      case FILE_MANAGE_ORDER.REMOVE:
        setRequiredDocument(newRequiredDocument.filter(doc => doc.id !== id));
        break;
      default:
    }
    return true;
  };

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Accordion
        defaultExpanded
        title={title}
        details={
          <ContentBox>
            <Container>
              <Grid
                container
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <h2 className="text-lg font-medium mb-3">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: description,
                    }}
                  />
                </h2>
                {Boolean(supplementText) && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: supplementText,
                    }}
                  />
                )}
              </Grid>
              <FilesPanel
                disable={!enableEdit}
                disableDeleteOnly={!enableDelete}
                maxFileNumber={5}
                title={filesPanelTitle}
                caption={filesPanelCaption}
                files={requiredDocument}
                handleFilesChange={handleUploadFile}
                handleLoading={handleLoading}
              />
            </Container>
            {enableEdit && (
              <div className="buttons-area">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleSubmitRequiredDocument}
                >
                  {PAGE_TEXT.SAVE}
                </Button>
              </div>
            )}
          </ContentBox>
        }
      />
    </>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  padding: 2rem 1rem;
`;
const ContentBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  .buttons-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    margin-bottom: 1rem;
  }
`;
